/**
 * Versionen und Papierkorb — `NC-003`, Einstiegsstelle nach `PORTAL-002`.
 *
 * Der Paketeintrag sagt, warum das hier steht und nicht im Adapter: „Ohne
 * diesen Ort läge die Funktion im Adapter und wäre für niemanden erreichbar —
 * der Fall ‚gestern war die Datei noch da' landet sonst beim Betreiber statt
 * beim Benutzer."
 *
 * **Beide Aktionen sagen vor der Ausführung, was sie tun** — `05` §5.12, und
 * `PORTAL-002` schreibt es aus:
 *
 *   Zurückholen   benennt den Stand, der geholt wird, und sagt, dass der
 *                 aktuelle als Version erhalten bleibt.
 *   Entfernen     benennt den Eintrag und ist nicht rückholbar.
 *
 * „Ein Knopf ‚Wiederherstellen' ohne Angabe des Standes ist keine Bestätigung,
 * sondern eine Zumutung." Deshalb steht in jedem Dialog, welche Fassung
 * gemeint ist — mit Datum, Größe und dem, was sich damals geändert hat.
 */

import { useState } from "react";

import { findeAblage, nachbarn } from "@hwc/demo-data";
import type { Ablageobjekt, Auftrag, Dateiversion } from "@hwc/demo-data";
import { darfEingangBearbeiten } from "@hwc/auth";
import { Dialog, Knopf, Zustand } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { entferneEndgueltig, holeZurueck } from "../../src/daten/vorgaenge.js";
import { Liste, Zeile, datum, groesse, uhrzeit } from "../../src/bereiche/bausteine.js";

export function Wiederherstellen({ auftrag }: { auftrag: Auftrag }) {
  const { bestand, rollen } = useSitzung();
  const [absage, setzeAbsage] = useState<string | null>(null);

  const objekte = nachbarn(bestand, auftrag.ref)
    .map((r) => findeAblage(bestand, r))
    .filter((o): o is Ablageobjekt => o !== undefined && o.art === "arbeitsdatei");

  /**
   * Der Papierkorb enthält, was am **Auftragsordner** hängt.
   *
   * Über den Pfad und nicht über den Link-Graphen: Eine gelöschte Datei
   * verliert regelmäßig ihre Kanten — wer eine Datei löscht, räumt oft auch
   * die Verknüpfung weg. Der Ordner bleibt.
   */
  const papierkorb = bestand.ablage.filter(
    (o) =>
      o.geloescht !== undefined
      && o.pfad?.includes(auftrag.nummer) === true,
  );

  const mitVersionen = objekte.filter((o) => (o.versionen?.length ?? 0) > 1);
  const darf = darfEingangBearbeiten(rollen);

  if (mitVersionen.length === 0 && papierkorb.length === 0) return null;

  return (
    <>
      <h2>Frühere Fassungen und Papierkorb</h2>

      {absage && <Zustand art="warnung" text={absage} />}

      {mitVersionen.map((o) => (
        <Versionsliste
          key={o.ref.opaqueId}
          objekt={o}
          darf={darf}
          beiAbsage={setzeAbsage}
        />
      ))}

      {papierkorb.length > 0 && (
        <>
          <h3>Papierkorb des Auftragsordners</h3>
          <Liste>
            {papierkorb.map((o) => (
              <Papierkorbzeile
                key={o.ref.opaqueId}
                objekt={o}
                darf={darf}
                beiAbsage={setzeAbsage}
              />
            ))}
          </Liste>
        </>
      )}

      <p className="notiz">
        Versionen und Papierkorb führt <strong>Nextcloud</strong>; das Portal ist die
        Stelle, an der die Frage entsteht (<code>NC-003</code>). Wie lange gelöschte
        Dateien dort liegen, entscheidet die Aufbewahrung des Kundenstacks —
        <strong> ein Papierkorb ist keine Sicherung.</strong>
      </p>
    </>
  );
}

function Versionsliste({
  objekt,
  darf,
  beiAbsage,
}: {
  objekt: Ablageobjekt;
  darf: boolean;
  beiAbsage: (grund: string | null) => void;
}) {
  const { person, fuehreAus } = useSitzung();
  const [gewaehlt, setzeGewaehlt] = useState<Dateiversion | null>(null);

  const versionen = objekt.versionen ?? [];
  const aktuell = versionen[0];

  return (
    <>
      <h3>{objekt.name}</h3>
      <Liste>
        {versionen.map((v, i) => (
          <Zeile
            key={v.kennung}
            titel={
              i === 0 ? `${v.kennung} — aktuelle Fassung` : v.kennung
            }
            unterzeile={
              <>
                {datum(v.zeitpunkt)} {uhrzeit(v.zeitpunkt)}
                {v.geaendertVon ? ` · ${v.geaendertVon}` : ""}
                {v.notiz ? ` · ${v.notiz}` : ""}
              </>
            }
            rechts={
              i === 0 ? (
                groesse(v.groesseBytes)
              ) : darf ? (
                <Knopf type="button" art="neben" onClick={() => setzeGewaehlt(v)}>
                  Zurückholen
                </Knopf>
              ) : (
                groesse(v.groesseBytes)
              )
            }
          />
        ))}
      </Liste>

      <Dialog
        offen={gewaehlt !== null}
        beiSchliessen={() => setzeGewaehlt(null)}
        titel="Frühere Fassung zurückholen?"
      >
        {gewaehlt && (
          <>
            {/*
              `PORTAL-002`: „Zurückholen benennt den Stand, der geholt wird, und
              sagt, dass der aktuelle als Version erhalten bleibt." Beides steht
              hier, weil ein Knopf ohne diese Angabe keine Bestätigung ist.
            */}
            <p>
              <strong>{objekt.name}</strong> wird auf die Fassung{" "}
              <strong>{gewaehlt.kennung}</strong> vom {datum(gewaehlt.zeitpunkt)}{" "}
              {uhrzeit(gewaehlt.zeitpunkt)} zurückgesetzt
              {gewaehlt.geaendertVon ? ` (${gewaehlt.geaendertVon})` : ""}.
            </p>
            {gewaehlt.notiz && <p className="notiz">Damals: {gewaehlt.notiz}</p>}
            <p>
              Die aktuelle Fassung {aktuell ? `(${aktuell.kennung})` : ""}{" "}
              <strong>geht nicht verloren</strong> — sie bleibt als Version erhalten.
              Rückgängig machen heißt: die neuere wieder zurückholen.
            </p>
            <div className="filter">
              <Knopf
                type="button"
                art="haupt"
                onClick={() => {
                  const grund = fuehreAus((v) =>
                    holeZurueck({
                      vorgaenge: v,
                      datei: objekt.ref,
                      name: objekt.name,
                      version: gewaehlt.kennung,
                      stand: gewaehlt.zeitpunkt,
                      akteur: person.ref,
                    }),
                  );
                  beiAbsage(grund ?? null);
                  setzeGewaehlt(null);
                }}
              >
                {gewaehlt.kennung} zurückholen
              </Knopf>
              <Knopf type="button" art="neben" onClick={() => setzeGewaehlt(null)}>
                Abbrechen
              </Knopf>
            </div>
          </>
        )}
      </Dialog>
    </>
  );
}

function Papierkorbzeile({
  objekt,
  darf,
  beiAbsage,
}: {
  objekt: Ablageobjekt;
  darf: boolean;
  beiAbsage: (grund: string | null) => void;
}) {
  const { person, fuehreAus } = useSitzung();
  const [entfernenOffen, setzeEntfernen] = useState(false);

  return (
    <>
      <Zeile
        titel={objekt.name}
        unterzeile={
          <>
            gelöscht {objekt.geloescht ? datum(objekt.geloescht) : ""}
            {objekt.geloeschtVon ? ` von ${objekt.geloeschtVon}` : ""} · {objekt.pfad}
          </>
        }
        rechts={
          darf ? (
            <>
              <Knopf
                type="button"
                art="neben"
                onClick={() => {
                  const grund = fuehreAus((v) =>
                    holeZurueck({
                      vorgaenge: v,
                      datei: objekt.ref,
                      name: objekt.name,
                      akteur: person.ref,
                    }),
                  );
                  beiAbsage(grund ?? null);
                }}
              >
                Zurückholen
              </Knopf>{" "}
              <Knopf type="button" art="still" onClick={() => setzeEntfernen(true)}>
                Endgültig entfernen
              </Knopf>
            </>
          ) : (
            groesse(objekt.groesseBytes)
          )
        }
      />

      <Dialog
        offen={entfernenOffen}
        beiSchliessen={() => setzeEntfernen(false)}
        titel="Endgültig entfernen?"
      >
        {/*
          `PORTAL-002`: „endgültiges Entfernen benennt den Eintrag und ist
          nicht rückholbar." Der Name steht deshalb im Satz und nicht nur in
          der Zeile darüber — wer den Dialog liest, hat die Liste womöglich
          schon aus dem Blick.
        */}
        <p>
          <strong>{objekt.name}</strong> wird aus dem Papierkorb entfernt.
        </p>
        <p>
          <strong>Das ist nicht rückholbar.</strong> Danach hilft nur noch die Sicherung
          — und die reicht nach ADR-029 bis zu vierundzwanzig Stunden zurück, nicht
          weiter.
        </p>
        <div className="filter">
          <Knopf
            type="button"
            art="haupt"
            onClick={() => {
              const grund = fuehreAus((v) =>
                entferneEndgueltig({
                  vorgaenge: v,
                  datei: objekt.ref,
                  name: objekt.name,
                  akteur: person.ref,
                }),
              );
              beiAbsage(grund ?? null);
              setzeEntfernen(false);
            }}
          >
            Ja, endgültig entfernen
          </Knopf>
          <Knopf type="button" art="neben" onClick={() => setzeEntfernen(false)}>
            Abbrechen
          </Knopf>
        </div>
      </Dialog>
    </>
  );
}
